import { useEffect, useRef } from "react";

import BoltIcon from "@mui/icons-material/Bolt";
import { Stack, Tooltip, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent, EVOTRUX_BLUE } from "constants/index";
import { useShipperBookingStats } from "hooks/useShipperBookingStats";
import { formatBookingDurationSeconds } from "utils/time";

interface ShipperBookingSpeedIndicatorProps {
  shipperRoleId: number;
}

export function ShipperBookingSpeedIndicator(
  props: ShipperBookingSpeedIndicatorProps,
) {
  const { shipperRoleId } = props;
  const posthog = usePostHog();
  const { avgBookingDurationSeconds, loading } = useShipperBookingStats({
    shipperRoleId,
  });
  const hasTrackedViewed = useRef(false);

  useEffect(() => {
    hasTrackedViewed.current = false;
  }, [shipperRoleId]);

  useEffect(() => {
    if (
      !loading &&
      avgBookingDurationSeconds !== null &&
      !hasTrackedViewed.current
    ) {
      hasTrackedViewed.current = true;
      posthog?.capture(ClientAppEvent.SHIPPER_BOOKING_STATS_VIEWED);
    }
  }, [loading, avgBookingDurationSeconds, posthog]);

  if (loading || avgBookingDurationSeconds === null) {
    return null;
  }

  return (
    <Tooltip title="Average time this shipper takes to book a shipment after posting it.">
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ cursor: "default" }}
      >
        <Typography variant="body2" color="text.secondary">
          (
          <BoltIcon
            sx={{ fontSize: 16, color: EVOTRUX_BLUE, verticalAlign: "middle" }}
          />
          {`This shipper usually books within ${formatBookingDurationSeconds(avgBookingDurationSeconds)}`}
          )
        </Typography>
      </Stack>
    </Tooltip>
  );
}
